import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ISO_WEEKDAYS, REPORT_SCHEDULE_LAST_DAY_OF_MONTH } from '@brightlocal/shared';

import { SCHEDULE_FORM_FIELDS, SCHEDULE_FREQUENCY, SCHEDULE_MONTH_DAYS, SCHEDULE_TIME_SLOTS } from '../consts';

const WeekdaySchema = z.nativeEnum(ISO_WEEKDAYS);

/** A day the Date select lists, or the last day of the month. */
const MonthDaySchema = z.union([
  z.number().int().min(1).max(SCHEDULE_MONTH_DAYS.length),
  z.literal(REPORT_SCHEDULE_LAST_DAY_OF_MONTH)
]);

export const useScheduleSchema = () => {
  const { t } = useTranslation();

  return z
    .object({
      [SCHEDULE_FORM_FIELDS.FREQUENCY]: z.nativeEnum(SCHEDULE_FREQUENCY),
      [SCHEDULE_FORM_FIELDS.RUN_ON]: z.number().nullable(),
      [SCHEDULE_FORM_FIELDS.TIME_SLOT]: z.enum(SCHEDULE_TIME_SLOTS).nullable()
    })
    .superRefine(({ scheduleFrequency, scheduleRunOn, scheduleTimeSlot }, context) => {
      const isWeekly = scheduleFrequency === SCHEDULE_FREQUENCY.WEEKLY;
      const isMonthly = scheduleFrequency === SCHEDULE_FREQUENCY.MONTHLY;
      const isMissingDay = isWeekly && !WeekdaySchema.safeParse(scheduleRunOn).success;
      const isMissingDate = isMonthly && !MonthDaySchema.safeParse(scheduleRunOn).success;
      const isMissingTimeSlot = (isWeekly || isMonthly) && scheduleTimeSlot === null;

      if (isMissingDay) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [SCHEDULE_FORM_FIELDS.RUN_ON],
          message: t('reportSchedule.validation.dayRequired')
        });
      }

      if (isMissingDate) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [SCHEDULE_FORM_FIELDS.RUN_ON],
          message: t('reportSchedule.validation.dateRequired')
        });
      }

      if (isMissingTimeSlot) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [SCHEDULE_FORM_FIELDS.TIME_SLOT],
          message: t('reportSchedule.validation.timeSlotRequired')
        });
      }
    });
};

/** Values of the schedule form, in the shape the report settings API takes. */
export type ScheduleFormValues = z.infer<ReturnType<typeof useScheduleSchema>>;
